import { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { Box, Button, Checkbox, FormControlLabel, Link, Paper, Typography } from '@mui/material'
import { BATIKAL_PRIVACY_URL, updateUser } from '@batical/common'
import { selectUser, signCGUAction } from '@batical/reducer/src'
import { useTranslation } from 'react-i18next'

import Page from 'src/components/Page'
import { CguAcceptPopupBody } from 'src/components/CguAcceptPopupBody'

const CGU_VER = Number(import.meta.env.VITE_CGU_VERSION ?? 3)

export function CGUAcceptPage() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const user = useSelector(selectUser)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const signed = user.cguVersionSigned === CGU_VER

  const onToggle = useCallback(
    async (accept: boolean) => {
      if (!user.id) return
      setBusy(true)
      setError(null)
      try {
        const version = accept ? CGU_VER : 0
        const at = accept ? Date.now() : 0
        await updateUser(user.id, {
          cguVersionSigned: version,
          cguVersionSignedDate: at
        })
        dispatch(signCGUAction(version, at))
      } catch {
        setError("Impossible d'enregistrer votre acceptation. Vérifiez votre connexion et réessayez.")
      } finally {
        setBusy(false)
      }
    },
    [dispatch, user.id]
  )

  const onContinue = useCallback(() => {
    navigate('/dashboard/sections', { replace: true })
  }, [navigate])

  return (
    <Page title="BatiKal | Acceptation CGU">
      <Paper sx={{ p: { xs: 2, sm: 3 }, maxWidth: 720, mx: 'auto' }}>
        <CguAcceptPopupBody />

        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.65, mb: 2 }}>
          {t('cgu.message2')}
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            Documents complets
          </Typography>
          <Link component={RouterLink} to="/cgu" sx={{ mr: 2 }}>
            Conditions générales (CGU)
          </Link>
          <Link href={BATIKAL_PRIVACY_URL} target="_blank" rel="noopener noreferrer">
            Politique de confidentialité
          </Link>
        </Box>

        {error && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={signed}
              disabled={busy || !user.id}
              onChange={(_, checked) => {
                void onToggle(checked)
              }}
            />
          }
          label={t('cgu.acceptLabel')}
        />
        <Box sx={{ mt: 3 }}>
          <Button variant="contained" disabled={!signed || busy} onClick={onContinue} sx={{ textTransform: 'none' }}>
            Continuer
          </Button>
        </Box>
      </Paper>
    </Page>
  )
}
